import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AwaitingTimeoutProgress from "../Feedback/AwaitingTimeoutProgress";
import HttpFetchStatus from "../../enums/api/http-fetch-status";
import SignalRConnectionState from "../../enums/api/signalr-connection-state";
import HttpError from "../../interfaces/api/http-error";

interface RebootPopupContentProps {
    onRebootFinished: () => void;
    forceReboot?: boolean
    rebootData: {
        getStatus: { status: HttpFetchStatus; error: HttpError | null };
        postStatus: { status: HttpFetchStatus; error: HttpError | null };
        isRebootRequired: boolean;
    };
    hostHubConnectionStatus: SignalRConnectionState | null;
    updateTimeoutData: { length: number; startTime: number | null };
    onGetIsRebootRequired: () => void;
    onPostStartReboot: () => void;
}

const RebootPopupContent: React.FC<RebootPopupContentProps> = ({
    onRebootFinished,
    forceReboot,
    rebootData,
    hostHubConnectionStatus,
    updateTimeoutData,
    onGetIsRebootRequired,
    onPostStartReboot
}) => {
    const { t } = useTranslation();

    const [isRebootInProgress, setIsRebootInProgress] = useState(false);
    const [isSystemDown, setIsSystemDown] = useState(false);

    useEffect(() => {
      if (rebootData.getStatus.status === HttpFetchStatus.Idle && forceReboot !== true) {
        onGetIsRebootRequired();
      } else if (
        isRebootInProgress === false &&
        ((rebootData.getStatus.status === HttpFetchStatus.Success && rebootData.isRebootRequired) ||
          forceReboot === true)
      ) {
        onPostStartReboot();
        setIsRebootInProgress(true);
      } else if (
        rebootData.getStatus.status === HttpFetchStatus.Success &&
        !rebootData.isRebootRequired &&
        !forceReboot
      ) {
        onRebootFinished();
      }
    }, [
      forceReboot,
      isRebootInProgress,
      onRebootFinished,
      onGetIsRebootRequired,
      onPostStartReboot,
      rebootData.getStatus.status,
      rebootData.isRebootRequired,
    ]);

    useEffect(() => {
        if (isRebootInProgress && hostHubConnectionStatus === SignalRConnectionState.Connected && isSystemDown) {
            onRebootFinished();
        } else if (isRebootInProgress && hostHubConnectionStatus === SignalRConnectionState.Disconnected) {
            setIsSystemDown(true);
        }
    }, [hostHubConnectionStatus, isRebootInProgress, isSystemDown, onRebootFinished]);

    const currentTime = new Date().getTime() / 1000;
    const timeoutRemaining = Math.max(
        updateTimeoutData.length - (currentTime - (updateTimeoutData.startTime ?? currentTime)),
        0
    );

    return (
        <React.Fragment>
            {forceReboot !== true &&
                (rebootData.getStatus.status === HttpFetchStatus.Loading ||
                    rebootData.getStatus.status === HttpFetchStatus.Idle) && (
                    <AwaitingTimeoutProgress
                        timeoutSeconds={timeoutRemaining}
                        progressLabel={t("is-reboot-required-check-in-progress")}
                        onTimeOutReached={() => null}
                    />
                )}
            {isRebootInProgress && (
                <AwaitingTimeoutProgress
                    timeoutSeconds={timeoutRemaining}
                    progressLabel={t("rebooting-in-progress")}
                    onTimeOutReached={() => null}
                />
            )}
        </React.Fragment>
    );
};

export default RebootPopupContent;